import {
  CreatePendingSeatingBookingRequest,
  CreateSeatingBookingRequest,
  GetSevenRoomsAvailabilityV1Params,
  GetSevenRoomsSeatingAreasV1Params,
  GetSevenRoomsVenuesV1Params,
  SeatingAreaDto,
  SeatingClientTagGroupDto,
  SeatingReservationDto,
  VenueDto,
  ShiftAvailabilityDto,
} from '@/services/platform/api/model';
import {
  bookSevenRoomsReservationV1,
  getSevenRoomsAvailabilityV1,
  getSevenRoomsClientTagsV1,
  getSevenRoomsSeatingAreasV1,
  getSevenRoomsVenuesV1,
  requestSevenRoomsReservationV1,
} from '../../../api/sevenrooms-reservations/sevenrooms-reservations';

/**
 * A SevenRooms booking/request call failed. `reason` is the backend's `ProblemDetails.detail`
 * code (e.g. `"sevenrooms_rejected"`) when the failure came back as an HTTP error response;
 * `undefined` for a network-level failure (no response at all).
 */
export class SevenRoomsBookingError extends Error {
  constructor(public reason?: string) {
    super(reason ?? 'unknown');
  }
}

/**
 * Fetches venues that offer SevenRooms bookable services.
 *
 * Only venues with at least one published, active `BookableService` whose
 * `ServiceType` is Restaurant or Bar are returned. Both filter params are
 * optional — omitting them returns all qualifying venues for the instance.
 *
 * @param params - Optional query filters:
 *   - `locationNodeKey` — restrict results to a specific location node.
 *   - `serviceCategoryNodeKey` — restrict to venues reachable through a
 *     specific service category (the category the user entered the flow through).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so venue names and labels are returned in the correct language.
 * @returns Array of `VenueDto` on HTTP 200, ordered as returned by the API.
 * @returns `undefined` on HTTP 500 or network error.
 */
export async function getSevenRoomsVenues(
  params?: GetSevenRoomsVenuesV1Params,
  locale?: string
): Promise<VenueDto[] | undefined> {
  try {
    const response = await getSevenRoomsVenuesV1(params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as VenueDto[];
    }

    console.warn(`getSevenRoomsVenues: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getSevenRoomsVenues: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches seating areas available inside a specific SevenRooms venue.
 *
 * Only areas belonging to a published, active venue are returned. The optional
 * `serviceCategoryNodeKey` narrows results to areas reachable through the
 * service category the user entered the flow through (matches the operator's
 * matrix assignment).
 *
 * @param venueNodeKey - Node key of the venue whose seating areas to fetch.
 * @param params - Optional query filters:
 *   - `serviceCategoryNodeKey` — restrict to areas assigned to a specific
 *     service category.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so area names are returned in the correct language.
 * @returns Array of `SeatingAreaDto` on HTTP 200, ordered as returned by the API.
 * @returns `undefined` on HTTP 500 or network error.
 */
export async function getSevenRoomsSeatingAreas(
  venueNodeKey: string,
  params?: GetSevenRoomsSeatingAreasV1Params,
  locale?: string
): Promise<SeatingAreaDto[] | undefined> {
  try {
    const response = await getSevenRoomsSeatingAreasV1(venueNodeKey, params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as SeatingAreaDto[];
    }

    console.warn(`getSevenRoomsSeatingAreas: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getSevenRoomsSeatingAreas: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches the full shift grid (Lunch/Dinner) for a venue on a given date,
 * with every time slot pre-computed and cross-referenced against SevenRooms
 * availability.
 *
 * Each `ShiftAvailabilityDto` represents one shift (e.g. Lunch, Dinner) and
 * carries its time boundaries, slot interval, and the list of slots. Each slot
 * exposes `isAvailable` and the seating areas that can serve it. Slots already
 * in the past (Europe/Lisbon) are returned as unavailable.
 *
 * @param venueNodeKey - Node key of the venue to query availability for.
 * @param params - Optional query filters:
 *   - `date` — ISO 8601 date string (e.g. `"2025-08-15"`); defaults to today
 *     when omitted.
 *   - `guestCount` — guest count as a string; used to pre-filter slots that
 *     cannot accommodate the party size.
 *   - `seatingAreaPublicGuid` — restrict availability to a single seating area.
 *   - `serviceCategoryNodeKey` — restrict to slots reachable through a specific
 *     service category.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so shift and slot labels are returned in the correct language.
 * @returns Array of `ShiftAvailabilityDto` on HTTP 200, ordered as returned by the API.
 * @returns `undefined` on HTTP 400 (bad request), HTTP 502 (upstream error), or
 *   network failure.
 */
export async function getSevenRoomsAvailability(
  venueNodeKey: string,
  params?: GetSevenRoomsAvailabilityV1Params,
  locale?: string
): Promise<ShiftAvailabilityDto[] | undefined> {
  try {
    const response = await getSevenRoomsAvailabilityV1(venueNodeKey, params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as ShiftAvailabilityDto[];
    }

    console.warn(`getSevenRoomsAvailability: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getSevenRoomsAvailability: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches the client tag groups available for a specific SevenRooms venue.
 *
 * Tags are used during the booking flow to annotate a reservation with
 * guest preferences or occasion markers (e.g. "Anniversary", "Window seat").
 * They are grouped by category and sourced from SevenRooms via the venue's
 * integration configuration.
 *
 * @param venueNodeKey - Node key of the venue whose client tags to fetch.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so tag and group labels are returned in the correct language.
 * @returns Array of `SeatingClientTagGroupDto` on HTTP 200, each containing
 *   a `group` label and an array of `tags` strings.
 * @returns `undefined` on HTTP 500 (misconfigured integration), HTTP 502
 *   (upstream SevenRooms error), or network failure.
 */
export async function getSevenRoomsClientTags(
  venueNodeKey: string,
  locale?: string
): Promise<SeatingClientTagGroupDto[] | undefined> {
  try {
    const response = await getSevenRoomsClientTagsV1(venueNodeKey, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });

    if (response.status === 200) {
      return response.data as SeatingClientTagGroupDto[];
    }

    console.warn(`getSevenRoomsClientTags: unexpected status ${response.status}`);
    return undefined;
  } catch (error) {
    console.warn('getSevenRoomsClientTags: request failed:', error);
    return undefined;
  }
}

/**
 * Submits a pending SevenRooms reservation request.
 *
 * Calls `POST /api/v1/reservations/sevenrooms/booking-requests`. Used when
 * the selected slot is at capacity or requires approval — the reservation is
 * created with a Pending status and must be confirmed by the venue. An
 * `Idempotency-Key` header (client-generated GUID) is required to deduplicate
 * network retries — the server caches the first successful response for 24
 * hours per (user, key).
 *
 * @param request - Booking request payload:
 *   - `seatingAreaPublicGuid` — public GUID of the chosen seating area.
 *   - `date` — ISO 8601 date string (e.g. `"2025-08-15"`).
 *   - `time` — 24-hour time string (e.g. `"19:30"`).
 *   - `guestCount` — number of guests in the party.
 *   - `comments` — optional free-text note for the venue.
 * @param idempotencyKey - GUID generated at flow start and stored in `BookingState`,
 *   used as `Idempotency-Key` header to deduplicate retries.
 * @returns `SeatingReservationDto` with `status: "Pending"` on HTTP 200.
 * @throws {SevenRoomsBookingError} on HTTP 400 (bad request), HTTP 401 (unauthenticated),
 *   HTTP 502 (upstream SevenRooms error), or network failure.
 */
export async function requestSevenRoomsReservation(
  request: CreatePendingSeatingBookingRequest,
  idempotencyKey: string
): Promise<SeatingReservationDto> {
  let response;
  try {
    response = await requestSevenRoomsReservationV1(request, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  } catch (error) {
    console.warn('requestSevenRoomsReservation: request failed:', error);
    throw new SevenRoomsBookingError();
  }

  if (response.status === 200) {
    return response.data as SeatingReservationDto;
  }

  // `data` is typed `void` on error statuses by the generated client (the backend's OpenAPI
  // spec doesn't declare a body for them), but `customFetch` parses the body regardless of
  // status — it's actually a ProblemDetails with a `detail` code (e.g. "sevenrooms_rejected").
  const reason = (response.data as unknown as { detail?: string } | null)?.detail;
  console.warn(`requestSevenRoomsReservation: unexpected status ${response.status}`, reason);
  throw new SevenRoomsBookingError(reason);
}

/**
 * Submits a confirmed SevenRooms reservation.
 *
 * Calls `POST /api/v1/reservations/sevenrooms/bookings`. The slot must be
 * available; the API returns 400 if it is not. An `Idempotency-Key` header
 * (client-generated GUID) is required to deduplicate network retries — the
 * server caches the first successful response for 24 hours per (user, key).
 *
 * @param request - Booking payload:
 *   - `seatingAreaPublicGuid` — public GUID of the chosen seating area.
 *   - `date` — ISO 8601 date string (e.g. `"2025-08-15"`).
 *   - `time` — 24-hour time string (e.g. `"19:30"`).
 *   - `guestCount` — number of guests in the party.
 *   - `comments` — optional free-text note for the venue.
 *   - `selectedTags` — optional tag keys applied to the reservation.
 * @param idempotencyKey - GUID generated at flow start and stored in `BookingState`,
 *   used as `Idempotency-Key` header to safely retry without double-booking.
 * @returns `SeatingReservationDto` on HTTP 200 (reservation confirmed).
 * @throws {SevenRoomsBookingError} on HTTP 400 (slot unavailable or bad request),
 *   HTTP 401 (unauthenticated), HTTP 502 (upstream SevenRooms error),
 *   or network failure.
 */
export async function bookSevenRoomsReservation(
  request: CreateSeatingBookingRequest,
  idempotencyKey: string
): Promise<SeatingReservationDto> {
  let response;
  try {
    response = await bookSevenRoomsReservationV1(request, {
      headers: { 'Idempotency-Key': idempotencyKey },
    });
  } catch (error) {
    console.warn('bookSevenRoomsReservation: request failed:', error);
    throw new SevenRoomsBookingError();
  }

  if (response.status === 200) {
    return response.data as SeatingReservationDto;
  }

  // `data` is typed `void` on error statuses by the generated client (the backend's OpenAPI
  // spec doesn't declare a body for them), but `customFetch` parses the body regardless of
  // status — it's actually a ProblemDetails with a `detail` code (e.g. "sevenrooms_rejected").
  const reason = (response.data as unknown as { detail?: string } | null)?.detail;
  console.warn(`bookSevenRoomsReservation: unexpected status ${response.status}`, reason);
  throw new SevenRoomsBookingError(reason);
}
